import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const CREDIT_CARD_CODES = ['54'];
const DEBIT_CARD_CODES = ['55'];

const PaymentMeansSelect = ({
  id = 'payment_means_code',
  name = 'payment_means_code',
  label,
  value = [],
  errors,
  readOnly = false,
  disabled = false,
  helperText = '',
  onChange,
}) => {
  const { eInvoiceForm, validations } = useTranslatedMessages();
  const { payment_means_select: paymentMeansSelect } = eInvoiceForm ?? {};

  const resolvedLabel = label ?? paymentMeansSelect?.label ?? 'Payment Means';

  const paymentMeansModesList = useSelector((state) => state.codeLists.paymentMeansModesList) ?? {};

  const paymentMeansOptions = Object.entries(paymentMeansModesList).map(([code, method]) => ({
    value: code,
    label: method,
  }));

  // const errorMessage = get(errors, name, {})?.message;
  const errorMessage = get(errors, name, {})?.message || get(errors, 'payment_means', {})?.message;
  const combinedHelperText = [helperText, errorMessage].filter(Boolean).join(' * ');

  const hasCreditCardSelected = value.some((code) => CREDIT_CARD_CODES.includes(code));
  const hasDebitCardSelected = value.some((code) => DEBIT_CARD_CODES.includes(code));

  const getDisabledReason = (option) => {
    if (CREDIT_CARD_CODES.includes(option.value) && hasDebitCardSelected) {
      return validations?.payment_means?.credit_card_cannot_be_selected;
    }
    if (DEBIT_CARD_CODES.includes(option.value) && hasCreditCardSelected) {
      return validations?.payment_means?.debit_card_cannot_be_selected;
    }
    return '';
  };

  const handleDelete = (valueToDelete, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const updated = value.filter((code) => code !== valueToDelete);
    onChange?.(updated);
  };

  const handleChange = (e) => {
    const selectedCodes = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;

    const eligibleSelectedCodes = selectedCodes.filter((code) => {
      const option = paymentMeansOptions.find((opt) => opt.value === code);
      return option && !getDisabledReason(option);
    });

    onChange?.(eligibleSelectedCodes);
  };

  return (
    <FormControl fullWidth error={!!errorMessage} variant="outlined" size="small" disabled={disabled}>
      <InputLabel id={`${id || name}-label`} shrink={value.length > 0}>
        {resolvedLabel}
      </InputLabel>

      <Select
        labelId={`${id || name}-label`}
        id={id || name}
        multiple
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        disabled={disabled}
        input={<OutlinedInput label={resolvedLabel} notched={value.length > 0} />}
        renderValue={(selected) => {
          const safeSelected = Array.isArray(selected) ? selected : [];
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {safeSelected.map((code) => {
                const option = paymentMeansOptions.find((opt) => opt.value === code);
                return (
                  <Chip
                    disabled={readOnly || disabled}
                    key={code}
                    label={option ? `${option.value} - ${option.label}` : code}
                    onDelete={!readOnly && !disabled ? (event) => handleDelete(code, event) : undefined}
                    deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
                    size="small"
                  />
                );
              })}
            </Box>
          );
        }}
      >
        {paymentMeansOptions.map((opt) => {
          const disabledReason = getDisabledReason(opt);
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              disabled={Boolean(disabledReason)}
              sx={{
                opacity: disabledReason ? 0.5 : 1,
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                },
              }}
            >
              {disabledReason ? (
                <Tooltip title={disabledReason} placement="right">
                  <span>
                    {opt.value} - {opt.label}
                  </span>
                </Tooltip>
              ) : (
                `${opt.value} - ${opt.label}`
              )}
            </MenuItem>
          );
        })}
      </Select>

      {combinedHelperText && <FormHelperText>{combinedHelperText}</FormHelperText>}
    </FormControl>
  );
};

export default PaymentMeansSelect;
